
import { Component, Vue } from "vue-property-decorator";
import * as lodash from "lodash";
import * as Models from "@gigalot/data-models";
import DimssaButton, { ButtonState } from "@/components/shared/dimssa-button.vue";
import { changeTrackingComponentFragment } from "@/helpers/gql";
import { listProcessingResult, listProcessingResultCorrection } from "@/helpers/graphql-list-items";

@Component({
  components: {
    DimssaButton,
  },
})
export default class ProcessingResultCorrections extends Vue {
  moment = this.$store.state.moment;
  fromDate: string = "";
  toDate: string = "";
  fromDateMenu: boolean = false;
  toDateMenu: boolean = false;
  referenceFilter: string = "";
  processingFunction: "reprocess" | "reweigh" | "reimplant" = "reprocess";
  processingFunctionItems = [
    { text: "Reprocess", value: "reprocess" },
    { text: "Reweigh", value: "reweigh" },
    { text: "Reimplant", value: "reimplant" },
  ];
  processingResultHeaders = [
    { text: "Reference", value: "reference" },
    { text: "Time", value: "time" },
    { text: "Type", value: "processingFunction" },
    { text: "Num Processed", value: "numProcessed" },
  ];
  selectedProcessingResult: any = null;
  selectedProcessingResultCorrection: any = null;
  processingResultsWithCorrections: any[] = [];
  processingResultsWithoutCorrections: any[] = [];
  processingResultCorrections: any[] = [];
  refreshButtonState: ButtonState = "ready";
  saveButtonState: ButtonState = "ready";

  get filteredProcessingResultsWithCorrections() {
    return this.processingResultsWithCorrections.filter((processingResult) => this.matchesReference(processingResult));
  }

  get filteredProcessingResultsWithoutCorrections() {
    return this.processingResultsWithoutCorrections.filter((processingResult) => this.matchesReference(processingResult));
  }

  created() {
    if (this.$store.state.user.user?.email !== "dolan@gigalot.co.za") {
      this.$router.push({ name: "home" });
      return;
    }

    const today = this.$store.state.moment();
    this.toDate = today.format("YYYY-MM-DD");
    this.fromDate = today.clone().subtract(3, "months").format("YYYY-MM-DD");

    this.$store.commit("navFuncs", {
      save: undefined,
      back: () => {
        this.$router.push({ name: "home" });
      },
    });
  }

  async refresh() {
    this.refreshButtonState = "busy";

    try {
      const fromDate = this.moment(this.fromDate, "YYYY-MM-DD").startOf("day").valueOf();
      const toDate = this.moment(this.toDate, "YYYY-MM-DD").endOf("day").valueOf();
      const [listedProcessingResults, listedProcessingResultCorrections] = await Promise.all([
        listProcessingResult(this.processingFunction, fromDate, toDate),
        listProcessingResultCorrection(undefined, this.processingFunction, fromDate, toDate),
      ]);

      //This map contains processing result list items but good enough (don't expect full ProcessingResult)
      const processingResultsByGuid = new Map<string, any>(listedProcessingResults.map((processingResult: any) => [processingResult.guid, processingResult]));
      const processingResultToCorrection = new Map<string, any>();
      const correctionToProcessingResult = new Map<string, any>();

      for (const processingResultCorrection of listedProcessingResultCorrections) {
        processingResultToCorrection.set(processingResultCorrection.processingResultGuid, processingResultCorrection);

        let processingResult = processingResultsByGuid.get(processingResultCorrection.processingResultGuid);
        if (!processingResult) {
          processingResult = await this.getProcessingResult(processingResultCorrection.processingResultGuid);
          if (processingResult) {
            processingResultsByGuid.set(processingResult.guid, processingResult);
          }
        }

        correctionToProcessingResult.set(processingResultCorrection.guid, processingResult);
      }

      const correctionsForProcessingResults: any[] = (
        await Promise.all(
          Array.from(processingResultsByGuid.values()).map(async (processingResult: any) => {
            const processingResultCorrection = processingResultToCorrection.get(processingResult.guid);
            return processingResultCorrection || this.getProcessingResultCorrection(processingResult.guid);
          })
        )
      ).filter((processingResultCorrection) => !!processingResultCorrection);

      for (const processingResultCorrection of correctionsForProcessingResults) {
        processingResultToCorrection.set(processingResultCorrection.processingResultGuid, processingResultCorrection);
        correctionToProcessingResult.set(processingResultCorrection.guid, processingResultsByGuid.get(processingResultCorrection.processingResultGuid));
      }

      const processingResults = Array.from(processingResultsByGuid.values());
      this.processingResultsWithCorrections = processingResults.filter((processingResult) => processingResultToCorrection.has(processingResult.guid));
      this.processingResultsWithoutCorrections = processingResults.filter((processingResult) => !processingResultToCorrection.has(processingResult.guid));
      this.processingResultCorrections = Array.from(processingResultToCorrection.values());

      this.refreshButtonState = "success";
    } catch (err) {
      this.refreshButtonState = "error";
      console.error("refresh() error:", err);
    }
  }

  formatProcessingResultTime(time?: number) {
    return time === undefined || time === null ? "" : this.moment(time).format("YYYY-MM-DD HH:mm:ss");
  }

  formatCorrectedValue(processingResult: any, property: "reference" | "processingFunction") {
    const correction = this.processingResultCorrections.find(
      (processingResultCorrection) => processingResultCorrection.processingResultGuid === processingResult.guid
    );
    const correctedValue = correction?.[property];
    const oldValue = processingResult[property];

    return `${correctedValue} (was ${oldValue})`;
  }

  matchesReference(processingResult: any) {
    const filter = String(this.referenceFilter || "")
      .trim()
      .toLowerCase();
    if (!filter) return true;

    const correction = this.processingResultCorrections.find(
      (processingResultCorrection) => processingResultCorrection.processingResultGuid === processingResult.guid
    );

    return [processingResult.reference, correction?.reference].some(
      (reference) => reference !== undefined && reference !== null && String(reference).toLowerCase().includes(filter)
    );
  }

  selectProcessingResult(processingResult: any) {
    this.selectedProcessingResult = processingResult;
    this.saveButtonState = "ready";
    this.selectedProcessingResultCorrection = this.processingResultCorrections.find(
      (processingResultCorrection) => processingResultCorrection.processingResultGuid === processingResult.guid
    );

    if (!this.selectedProcessingResultCorrection) {
      this.selectedProcessingResultCorrection = new Models.ProcessingResultCorrection();
      this.selectedProcessingResultCorrection.processingResultGuid = processingResult.guid;
      this.selectedProcessingResultCorrection.processingResultTime = processingResult.time;
      this.selectedProcessingResultCorrection.reference = processingResult.reference;
      this.selectedProcessingResultCorrection.processingFunction = processingResult.processingFunction;
    }
  }

  unselectProcessingResult() {
    this.selectedProcessingResult = null;
    this.selectedProcessingResultCorrection = null;
    this.saveButtonState = "ready";
  }

  async saveProcessingResultCorrection() {
    if (!this.selectedProcessingResultCorrection) return;

    this.saveButtonState = "busy";

    try {
      const processingResultCorrection = lodash.cloneDeep(this.selectedProcessingResultCorrection) as Models.ProcessingResultCorrection;
      if (!processingResultCorrection.changeTracking) {
        processingResultCorrection.changeTracking = new Models.ChangeTrackComponent();
      }

      const json = await this.$store.dispatch(
        "graphQl",
        {
          gql: `mutation setProcessingResultCorrection($guid: String!, $processingResultCorrection: ProcessingResultCorrectionInput!) {
            setProcessingResultCorrection(guid: $guid, processingResultCorrection: $processingResultCorrection) {
              ...changeTrackingFields
            }
          }${changeTrackingComponentFragment}`,
          variables: {
            guid: this.$store.state.user.location.guid,
            processingResultCorrection,
          },
          timeout: 10 * 1000,
        },
        { root: true }
      );

      console.log(json);

      processingResultCorrection.changeTracking = json.data.setProcessingResultCorrection;
      this.selectedProcessingResultCorrection = processingResultCorrection;

      const correctionIndex = this.processingResultCorrections.findIndex((existingCorrection) => existingCorrection.guid === processingResultCorrection.guid);
      if (correctionIndex === -1) {
        this.processingResultCorrections.push(processingResultCorrection);
      } else {
        this.processingResultCorrections.splice(correctionIndex, 1, processingResultCorrection);
      }

      this.saveButtonState = "success";
    } catch (err) {
      this.saveButtonState = "error";
      console.error("saveProcessingResultCorrection() error:", err);
    }
  }

  async getProcessingResultCorrection(processingResultGuid: string) {
    console.log("getProcessingResultCorrection");
    const json = await this.$store.dispatch(
      "graphQl",
      {
        gql: `query getProcessingResultCorrection($guid: String!, $processingResultGuid: String!) {
          getProcessingResultCorrection(guid: $guid, processingResultGuid: $processingResultGuid) {
            typename
            guid
            processingResultGuid
            processingResultTime
            reference
            processingFunction
          }
        }`,
        variables: {
          guid: this.$store.state.user.location.guid,
          processingResultGuid,
        },
      },
      { root: true }
    );
    console.log(json.data.getProcessingResultCorrection);
    return json.data.getProcessingResultCorrection;
  }

  async getProcessingResult(processingResultGuid: string) {
    const json = await this.$store.dispatch(
      "graphQl",
      {
        gql: `query getProcessingResult($guid: String!, $processingResultGuid: String!) {
          getProcessingResult(guid: $guid, processingResultGuid: $processingResultGuid) {
            typename
            guid
            reference
            time
            processingFunction
          }
        }`,
        variables: {
          guid: this.$store.state.user.location.guid,
          processingResultGuid,
        },
      },
      { root: true }
    );

    const processingResult = json.data.getProcessingResult;
    if (!processingResult) return processingResult;

    const numProcessedJson = await this.$store.dispatch(
      "graphQl",
      {
        gql: `query numProcessedAnimals($guid: String!, $processingResultGuid: String!) {
          numProcessedAnimals(guid: $guid, processingResultGuid: $processingResultGuid)
        }`,
        variables: {
          guid: this.$store.state.user.location.guid,
          processingResultGuid,
        },
      },
      { root: true }
    );

    processingResult.numProcessed = numProcessedJson.data.numProcessedAnimals;
    return processingResult;
  }
}
